import { isNullableType } from "graphql";
import { AssetLink, SoilProfile } from "../../../types/types"
import { dcRound } from "../../../utils/misc"

const loadImages = (soilProfile: SoilProfile, df: any) => {
    let availableColumns = df.listColumns();
    const maxDepth =  availableColumns.includes('depth (cm)') ? df.stat.max('depth (cm)') : 0;
    let interval = 10;
    let modulus = 5;

    if(maxDepth <= 30) {
        interval = 2;
        modulus = 1;
    } else if(maxDepth <= 75) {
        interval = 10;
        modulus = 5;
    }
    let images = soilProfile.assetLinks
        .filter((link: AssetLink) => link.asset_type == 'depth_thumbnail' && link.value % interval === modulus)
        .map((link: AssetLink, index: number) => {
            return {
                name: link.value + 'cm',
                source: link.s3_key,
                xref: "x domain",
                x:0,
                //y: (index * interval) - (interval/2),
                y: (index * interval), //+ 5, // ORIGINAL
                yref: "y",
                opacity: 1,
                layer: "below",
                xanchor: "left",
                yanchor:"top",
                sizex:1,
                sizey:55
            };
        });
    if(!images.length) {
        images = soilProfile.assetLinks
            .filter((link: AssetLink) => link.asset_type == 'depth_snapshot' && link.value % interval === modulus)
            .map((link: AssetLink, index: number) => {
                return {
                    name: link.value + 'cm',
                    source: link.s3_key,
                    xref: "x domain",
                    x:0,
                    y: (index * interval),// + 5,
                    yref: "y",
                    opacity: 1,
                    layer: "below",
                    xanchor: "left",
                    yanchor:"top",
                    sizex:1,
                    sizey:55
                };
            });
    }
    return images;
}


export const texturePlot = (soilProfile: SoilProfile, df: any) => {
    const images = loadImages(soilProfile, df);
    let availableColumns = df.listColumns();
    return {
        title: "Texture",
        traces: [{
            x: availableColumns.includes('sand') ? df.toArray('sand') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Sand',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'yellow' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('silt') ? df.toArray('silt') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Silt',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('clay') ? df.toArray('clay') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Clay',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'red' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                range: [0, 1],
                title: { "text": "Texture" },
                domain: [0.5, 0.9],
                autorange: true
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: "depth (cm)", standoff: 0 },
                range: [60, 0],
                domain: [0, 0.9],
                autorange: true,
                dtick: 10.0
            },
            autosize: true,
            images: images,
        }
    }
}

export const colorPlot = (soilProfile: SoilProfile, df: any) => {
    const images = loadImages(soilProfile, df);
    let availableColumns = df.listColumns();
    return {
        title: "Color",
        traces: [{
            x: availableColumns.includes('mean_hue') ? df.toArray('mean_hue') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Hue',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            showTitle: false
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'darkgray' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_sat') ? df.toArray('mean_sat') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Saturation',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: '#28a745"' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                range: [0, 1],
                title: { "text": "Color" },
                domain: [0.5, 0.9],
                autorange: true,
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: "depth (cm)", standoff: 0 },
                range: [60, 0],
                domain: [0, 0.9],
                autorange: true,
                //tick0: 5.0,
                dtick: 10.0, // ORIGINAL
            },
            autosize: true,
            images: images,
        }
    }
}

export const structurePlot = (df: any) => {
    let availableColumns = df.listColumns();
    // If all pores values are between 0 and 1, we assume they are in percentage and multiply by 100
    let poresArray = []; 
    let modifiedPoresArray = []; 
    if (availableColumns.includes('pores')){
        poresArray = df.toArray('pores');
        const allValuesBetween0And1 = poresArray.every((value: number) => value >= 0 && value <= 1);
        modifiedPoresArray = allValuesBetween0And1 ? poresArray : poresArray.map((value: number) => value / 100);
    }
    // same as above but with 
    let succolarityArray = []; 
    let modifiedSuccolarityArray = []; 
    if (availableColumns.includes('succolarity')){
        succolarityArray = df.toArray('succolarity');
        const succolarityAllValuesBetween0And1 = succolarityArray.every((value: number) => value >= 0 && value <= 1);

        modifiedSuccolarityArray = succolarityAllValuesBetween0And1 ? succolarityArray : succolarityArray.map((value: number) => value / 100) ;
    }
    //console.log("103024 modifiedPoresArray", modifiedPoresArray);
    //console.log("103024 poresArray", poresArray);
    //console.log("103024 modifiedSuccolarityArray", modifiedSuccolarityArray);
    //console.log("103024 succolarityArray", succolarityArray);
    return {
        title: "Structure",
        traces: [{
            x: availableColumns.includes('qt') ? df.toArray('qt') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Penetration Resistance',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedPoresArray,
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Macroporosity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'green' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedSuccolarityArray,
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Pore Connectivity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'blue' },
            xaxis: 'x3',
            yaxis: 'y',
            orientation: 'h',
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            xaxis: {
                side: "top",
                type: "linear",
                range: [0, 1500],
                domain: [0, 0.25],
                autorange: true,
            },
            xaxis2: {
                side: "top",
                type: "linear",
                range: [0, 100],
                domain: [0.3, 0.55],
                autorange: true,
                tickformat: ',.0%',
            },
            xaxis3: {
                side: "top",
                type: "linear",
                range: [0, 100],
                domain: [0.6, 0.85],
                autorange: true,
                tickformat: ',.0%',
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: "depth (cm)" },
                range: [60, 0],
                domain: [0, 0.85],
                autorange: true,
                dtick: 10.0
            },
            autosize: true,
        }
    }
}

export const waterPlot = (soilProfile: SoilProfile, df: any) => {
    let availableColumns = df.listColumns();
    const images = loadImages(soilProfile, df);

    //(ORIGINAL, will use again once calibrations are improved)df = availableColumns.includes('moisture_cal') ? df.map((row:any) => row.set('moisture_cal', row.get('moisture_cal') / 100)) : null;
    df = df.withColumn('moisture_cal', (row:any) => Math.min(
            (availableColumns.includes('poros') 
                ? row.get('poros') 
                : 0
            ), 
            ((availableColumns.includes('moisture_cal') 
                ? (row.get('moisture_cal') < 0) 
                    ? 0 
                    : row.get('moisture_cal') 
                : 0) 
            / 100)
        ));
    // If all pores values are between 0 and 1, we assume they are in percentage and multiply by 100
    let wiltingPointArray = []; 
    let modifiedWiltingPointArray = []; 
    if ( availableColumns.includes('pwp')){
        wiltingPointArray = df.toArray('pwp');
        const allValuesBetween0And1 = wiltingPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedWiltingPointArray = allValuesBetween0And1 ? wiltingPointArray : wiltingPointArray.map((value: number) => value / 100);
    }
    let fcPointArray = []; 
    let modifiedfcPointArray = []; 
    if ( availableColumns.includes('fc')){
        fcPointArray = df.toArray('fc');
        const allValuesBetween0And1FC = fcPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedfcPointArray = allValuesBetween0And1FC ? fcPointArray : fcPointArray.map((value: number) => value / 100);
    }
    let mPointArray = []; 
    let modifiedmPointArray = []; 
    if (availableColumns.includes('moisture_cal')){
        mPointArray = df.toArray('moisture_cal');
        const allValuesBetween0And1M = mPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedmPointArray = allValuesBetween0And1M ? mPointArray : mPointArray.map((value: number) => value / 100);
    }

    //console.log("103024 modifiedWiltingPointArray", modifiedWiltingPointArray);
    //console.log("103024 modifiedfcPointArray", modifiedfcPointArray);
    //console.log("103024 modifiedmPointArray", modifiedmPointArray);


    return {
        title: "Water",
        traces: [{
            x: modifiedWiltingPointArray,
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Wilting Point',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedfcPointArray,
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Field Capacity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'green' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('poros') ? df.toArray('poros') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Saturation',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'blue' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x:  modifiedmPointArray,
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Moisture',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'darkblue' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "Soil Water", standoff: 0 },
                range: [0, 100],
                domain: [0.5, 0.9],
                autorange: true,
                tickformat: ',.0%',
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: "depth (cm)", standoff: 0 },
                range: [60, 0],
                domain: [0, 0.9],
                autorange: true,
                dtick: 10.0
            },
            autosize: true,
            images: images,
        }
    }
}
export const healthPlot = (soilProfile: SoilProfile, df: any) => {
    
    const images = loadImages(soilProfile, df);
    console.log("Z051624 soilProfile", soilProfile)
    console.log("Z051624 df", df)
  
    let availableColumns = df.listColumns();
    // Create bd (Bulk Density)
    df = availableColumns.includes('poros') ? df.withColumn('bd', (row: any) => 2.65*(1 - row.get('poros'))) : df;
    // Create soc_bd
    //df = df.withColumn('soc_bd', (row: any) => (row.get('bd') / 1.50 + (1 - row.get('bd')) / 2.66));
    // Create soc_bd #Schojonning 2017 BD = 1/(OM/( 1.127 + 0.373 * OM) + (1-OM) / (2.648 + 0.209  *CLAY))
    df = df.withColumn('soc_bd', (row: any) => (1/(row.get('om')/( 1.127 + 0.373 * row.get('om')) + (1-row.get('om')) / (2.648 + 0.209  * row.get('clay')))));
    // Create oc (Organic Carbon)
    df = df.withColumn('oc', (row: any) => (row.get('om') / 1.72));
    /*const bdData = satData.map((value:any, i:any) => {
        let bdValue = 2.65*(1 - value)
        return dcRound(bdValue)
    });
    const roundedBDData = bdData.map((value:any, i:any) => { // Bulk density is replaced with SOC_BD ()
        return dcRound(value)
    });*/
    availableColumns = df.listColumns();
    console.log("Z051624 availableColumns", availableColumns)
    const omData = availableColumns.includes('om') ? df.toArray('om') : [];
    const satData = availableColumns.includes('poros') ? df.toArray('poros') : [];
    const bdData = availableColumns.includes('bd') ? df.toArray('bd') : [];
    const SOCbdData = availableColumns.includes('bd') ? df.toArray('bd') : [];
    //const SOCbdData = availableColumns.includes('soc_bd') ? df.toArray('soc_bd') : [];
    
    const roundedOmData = omData.map((value:any, i:any) => {
        return dcRound(value / 1.72) // SOC = OM / 1.72
    });
    console.log("Z051624 SOCbdData", SOCbdData)
    const roundedSOC_BDData = SOCbdData.map((value:any, i:any) => {
        return dcRound(value) // SOC_BD = OM / 1.50 + (1-OM) / 2.66
    });
    
    console.log("103024 df", df)
    console.log("Z051624 roundedSOC_BDData", roundedSOC_BDData)
    console.log("Z051624 roundedOmData", roundedOmData)
    availableColumns = df.listColumns();

    let bdPointArray = []; 
    let modifiedbdPointArray = []; 
    if ( availableColumns.includes('bd')){
        bdPointArray = df.toArray('bd');
        const allValuesBetween0And1bd = bdPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedbdPointArray = allValuesBetween0And1bd ? bdPointArray : bdPointArray.map((value: number) => value / 100);
    }
    console.log("103024 bdPointArray", bdPointArray);
    console.log("103024 modifiedbdPointArray", modifiedbdPointArray);
    return {
        title: "Carbon",
        traces: [{
            x: availableColumns.includes('oc') ? df.toArray('oc') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Organic Carbon',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'brown' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
            customdata: roundedOmData,
            hovertemplate: '%{customdata}%',
            //hovertemplate: '%{customdata}<extra>%{y} cm depth</extra>'
        },
        {
            //x: modifiedbdPointArray,
            x: availableColumns.includes('bd') ? df.toArray('bd') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Bulk Density',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'gray' },
            xaxis: 'x3',
            yaxis: 'y',
            orientation: 'h',
            customdata: roundedSOC_BDData,
            hovertemplate: '%{customdata} gm/cm',
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y", "x3y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.3],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "Organic Carbon", standoff: 0 },
                range: [0, 1000],
                domain: [0.35, 0.6],
                autorange: true,
                //tickformat: ',.0%', // Will format chart as % values 
            },
            xaxis3: {
                side: "top",
                type: "linear",
                title: { text: "Bulk Density", standoff: 0 },
                range: [0, 1000],
                domain: [0.65, 0.9],
                autorange: true,
                //tickformat: ',.0%', // Will format chart as % values 
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: "depth (cm)", standoff: 0 },
                range: [60, 0],
                domain: [0, 0.9],
                autorange: true,
                dtick: 10.0
            },
            autosize: true,
            images: images,
        }
    }
}
export const conductivityPlot = (df: any) => {
    let availableColumns = df.listColumns();
    df = availableColumns.includes('moisture_cal') ? df.withColumn('moisture_cal', (row: any) => row.get('moisture_cal') / 100) : df;
    df = availableColumns.includes('vres_cal') ? df.withColumn('eca', (row: any) => Math.max(0, 1 / (0.0021594 * row.get('vres_cal')))) : df;
    df = availableColumns.includes('eca') ? df.withColumn('ece', (row: any) => Math.max(0, 5.5 * row.get('eca') - 0.5 )) : df;

    availableColumns = df.listColumns();
    return {
        title: "Conductivity",
        traces: [{
            x: availableColumns.includes('eca') ? df.toArray('eca') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [],  
            name: 'Apparent Conductivity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line:{color:'lightblue'},
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('s__salts') ? df.toArray('s__salts') : [],
            y: availableColumns.includes('depth (cm)') ? df.toArray('depth (cm)') : [], 
            name: 'Soluble Salts',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line:{color:'yellow'},
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "ECa", standoff: 0 },
                range: [0, 1500],
                domain: [0, 0.4],
                autorange: true,
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "Soluble Salts", standoff: 0 },
                range: [0, 1500],
                domain: [0.5, 0.9],
                autorange: true,
            },
            yaxis: {
                side: "left",
                type: "linear",
                range: [60, 0],
                domain: [0, 0.9],
                autorange: true,
                dtick: 10.0
            },
            autosize: true        }
    }
}